import React from 'react';
import { Book, BookOpen, Calendar, Star, User, Share2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StarRating } from './StarRating';
import { CoverImage } from './CoverImage';
import { ShareButton } from './ShareButton';
import { ContentWarningBadge } from './ContentWarningBadge';
import { cn } from '@/lib/utils';
import { READING_FORMAT_EMOJI } from '@/lib/readingFormatUtils';
import type { ReadingFormat } from '@/lib/readingFormatUtils';
import { memo } from 'react';
import { useNavigate } from 'react-router-dom';

interface BookData {
  id: string;
  title: string;
  subtitle?: string;
  authors?: string[];
  publishedYear?: number;
  coverUrl?: string;
  coverCacheUrl?: string;
  isbn13?: string;
  isbn10?: string;
  olid?: string;
  language?: string;
}

interface UserBookData {
  ratingstar?: number;
  ratingStar?: number;
  tags?: string[];
  shelf?: string;
  readAt?: string;
  privacy?: string;
  reading_format?: ReadingFormat;
}

interface BookCardProps {
  book: BookData;
  userBook?: UserBookData;
  onClick?: () => void;
  compact?: boolean;
  showRating?: boolean;
  showShare?: boolean;
  showContentWarnings?: boolean;
  className?: string;
}

export function BookCard({ 
  book, 
  userBook, 
  onClick, 
  compact = false,
  showRating = true,
  showShare = false,
  showContentWarnings = true,
  className 
}: BookCardProps) {
  const navigate = useNavigate();
  const rating = userBook?.ratingstar || userBook?.ratingStar || 0;

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Card 
      className={cn(
        "card-elevated group overflow-hidden",
        compact ? "h-auto" : "h-full",
        className
      )}
    >
      <CardContent className={cn("p-0", compact ? "flex gap-3 p-3" : "")}>
        {/* Book Cover */}
        <div className={cn(
          "flex-shrink-0",
          compact ? "" : "w-full"
        )}>
          <div onClick={onClick} className="cursor-pointer">
            <CoverImage
              bookId={book.id}
              isbn13={book.isbn13}
              isbn10={book.isbn10}
              olid={book.olid}
              title={book.title}
              author={book.authors?.[0]}
              coverUrl={book.coverUrl}
              coverCacheUrl={book.coverCacheUrl}
              size={compact ? "sm" : "md"}
              className="w-full"
            />
          </div>
        </div>

        {/* Book Info */}
        <div className={cn(
          "space-y-2 relative",
          compact ? "flex-1 min-w-0" : "p-4"
        )}>
          {/* Language badge - show if non-English */}
          {book.language && book.language !== 'en' && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 left-0 text-xs uppercase z-10"
            >
              {book.language}
            </Badge>
          )}

          {/* Reading format badge */}
          {userBook?.reading_format && !compact && (
            <Badge 
              variant="outline" 
              className="absolute -top-1 left-0 text-xs z-10"
              title={`Read as ${userBook.reading_format}`}
            >
              {READING_FORMAT_EMOJI[userBook.reading_format]}
            </Badge>
          )}
          
          {/* Share button */}
          {showShare && (
            <div className="absolute top-2 right-2 z-10" onClick={handleShare}>
              <ShareButton
                shareData={{
                  kind: 'book',
                  book_id: book.id,
                  book: {
                    id: book.id,
                    title: book.title,
                    authors: book.authors || [],
                    cover_url: book.coverUrl || book.coverCacheUrl,
                  }
                }}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
              />
            </div>
          )}
          
          {/* Shelf badge for compact view */}
          {userBook?.shelf && compact && (
            <Badge 
              variant="secondary" 
              className="absolute -top-1 right-0 text-xs"
            >
              {userBook.shelf}
            </Badge>
          )}
          
          {/* Regular shelf badge for non-compact */}
          {userBook?.shelf && !compact && !showShare && (
            <Badge 
              variant="secondary" 
              className="absolute top-2 right-2 text-xs"
            >
              {userBook.shelf}
            </Badge>
          )}
          <div onClick={onClick} className="cursor-pointer">
            <h3 className={cn(
              "font-heading font-semibold leading-tight text-balance line-clamp-2",
              compact ? "text-sm" : "text-base"
            )}>
              {book.title}
            </h3>
            
            {book.subtitle && !compact && (
              <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
                {book.subtitle}
              </p>
            )}
          </div>

          {book.authors && book.authors.length > 0 && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <User className="w-3 h-3" />
              <span className="line-clamp-1">
                {book.authors.map((author, index) => (
                  <React.Fragment key={author}>
                    <a 
                      href={`/author/${encodeURIComponent(author)}`}
                      className="hover:text-primary hover:underline transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        navigate(`/author/${encodeURIComponent(author)}`);
                      }}
                    >
                      {author}
                    </a>
                    {index < book.authors.length - 1 && ', '}
                  </React.Fragment>
                ))}
              </span>
            </div>
          )}

          {book.publishedYear && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Calendar className="w-3 h-3" />
              <span>{book.publishedYear}</span>
            </div>
          )}

          {showRating && rating > 0 && (
            <div className="flex items-center gap-2">
              <StarRating 
                value={rating} 
                readonly 
                size={compact ? "sm" : "md"} 
              />
              <span className="text-sm text-muted-foreground">
                {rating.toFixed(1)}
              </span>
            </div>
          )}

          {userBook?.tags && userBook.tags.length > 0 && !compact && (
            <div className="flex flex-wrap gap-1">
              {userBook.tags.slice(0, 3).map((tag, index) => (
                <Badge 
                  key={index} 
                  variant="outline" 
                  className="text-xs px-2 py-0.5"
                >
                  {tag}
                </Badge>
              ))}
              {userBook.tags.length > 3 && (
                <Badge 
                  variant="outline" 
                  className="text-xs px-2 py-0.5"
                >
                  +{userBook.tags.length - 3}
                </Badge>
              )}
            </div>
          )}

          {userBook?.readAt && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <BookOpen className="w-3 h-3" />
              <span>Read {new Date(userBook.readAt).toLocaleDateString()}</span>
            </div>
          )}

          {/* Content Warning Badge */}
          {showContentWarnings && !compact && (
            <ContentWarningBadge bookId={book.id} className="absolute bottom-2 right-2" />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default memo(BookCard);